<template>
    <div data-cy="dropzone">
        <!-- Image preview (single image mode) -->
        <div v-if="!documents && previewUrl" class="tw:text-center tw:mb-3">
            <img :src="previewUrl" class="tw:max-h-48 tw:mx-auto tw:rounded tw:border tw:border-gray-200" />
            <Button variant="ghost" customClass="tw:mt-3" @click="showDeleteConfirm">
                <i class="fas fa-trash tw:mr-1"></i> Eliminar imagen
            </Button>
        </div>

        <!-- Dropzone area -->
        <div
            v-else
            class="tw:relative tw:border-2 tw:border-dashed tw:rounded-lg tw:p-5 tw:cursor-pointer tw:transition-all tw:duration-200"
            :class="dropzoneClasses"
            @dragover.prevent
            @dragenter.prevent="dragCounter++"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="onDrop"
            @click="triggerFileInput"
            @keydown.enter.prevent="triggerFileInput"
            @keydown.space.prevent="triggerFileInput"
            tabindex="0"
        >
            <input
                ref="fileInput"
                type="file"
                :accept="acceptFormatsInput"
                class="tw:hidden"
                @change="onFileSelected"
            />

            <div class="tw:flex tw:flex-col tw:items-center tw:gap-1.5 tw:text-gray-400">
                <!-- Empty state -->
                <template v-if="!uploading">
                    <svg class="tw:w-7 tw:h-7" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                    <p class="tw:text-sm tw:font-medium">
                        {{ documents ? 'Arrastra un documento o haz clic para subir' : 'Haz clic para subir o arrastra una imagen' }}
                    </p>
                    <p class="tw:text-xs">
                        {{ acceptedHint }}
                    </p>
                </template>

                <!-- Uploading state -->
                <div v-else class="tw:flex tw:items-center tw:gap-2">
                    <svg class="tw:w-4 tw:h-4 tw:animate-spin tw:text-blue-500" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="tw:opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span class="tw:text-sm tw:text-blue-600">{{ uploadingName }}</span>
                </div>
            </div>

            <p v-if="uploadError" class="tw:mt-2 tw:text-sm tw:text-red-500">{{ uploadError }}</p>
        </div>

        <!-- Modal de confirmación: debe estar siempre montado.
             vue-js-modal requiere presencia en el DOM para resolver show()/hide(). -->
        <BorrarModalComponent
            name="dropzone-delete-image"
            title="Eliminar imagen"
            @confirm="confirmRemove"
        >
            ¿Está segur@ de que desea eliminar esta imagen?
        </BorrarModalComponent>

        <!-- Thumbnails of uploaded documents -->
        <div v-if="documents?.length" class="tw:flex tw:flex-wrap tw:gap-2 tw:mt-4">
            <div
                v-for="doc in documents"
                :key="doc.id"
                class="tw:relative tw:w-36 tw:bg-white tw:border tw:border-gray-200 tw:rounded-lg tw:p-2 tw:flex tw:flex-col tw:items-center tw:gap-1.5 tw:transition-all tw:hover:shadow-sm tw:hover:border-gray-300"
            >
                <!-- Image thumbnail -->
                <button
                    v-if="esImagen(doc.ruta_fichero)"
                    type="button"
                    class="tw:w-full tw:aspect-square tw:rounded tw:overflow-hidden tw:bg-gray-50 tw:relative tw:group tw:p-0 tw:border-0 tw:cursor-zoom-in focus-visible:tw:ring-2 focus-visible:tw:ring-blue-400"
                    :title="`Ver ${doc.nombre}`"
                    @click.stop="$emit('view-document', doc)"
                >
                    <img
                        :src="doc.url"
                        class="tw:w-full tw:h-full tw:object-cover tw:transition-transform tw:duration-200 tw:group-hover:scale-105"
                        :alt="doc.nombre"
                    />
                    <span class="tw:absolute tw:inset-0 tw:bg-black/0 tw:group-hover:bg-black/30 tw:transition-colors tw:flex tw:items-center tw:justify-center tw:pointer-events-none">
                        <i class="fas fa-expand tw:text-white tw:text-base tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity"></i>
                    </span>
                </button>
                <!-- PDF icon -->
                <div
                    v-else
                    class="tw:w-full tw:aspect-square tw:rounded tw:bg-gray-50 tw:flex tw:items-center tw:justify-center"
                >
                    <i class="fas fa-file-pdf tw:text-xl tw:text-red-400"></i>
                </div>

                <!-- File name -->
                <span class="tw:text-[11px] tw:text-gray-500 tw:text-center tw:truncate tw:max-w-full tw:leading-tight">
                    {{ doc.nombre }}
                </span>

                <!-- Actions -->
                <div class="tw:absolute tw:top-2 tw:right-2 tw:p-1 tw:flex tw:gap-1 tw:bg-black/5 tw:rounded-md">
                    <Button
                        ignore-base-classes
                        custom-class="tw:w-6 tw:h-6 tw:rounded-full tw:bg-white/90 tw:shadow-sm tw:border tw:border-gray-200 tw:flex tw:items-center tw:justify-center tw:text-gray-400 tw:hover:text-blue-600 tw:hover:border-blue-300 tw:transition-colors"
                        icon="fas fa-download tw:text-xs"
                        title="Descargar documento"
                        @click.stop="$emit('download-document', doc)"
                    />
                    <Button
                        ignore-base-classes
                        custom-class="tw:w-6 tw:h-6 tw:rounded-full tw:bg-white/90 tw:shadow-sm tw:border tw:border-gray-200 tw:flex tw:items-center tw:justify-center tw:text-gray-400 tw:hover:text-blue-600 tw:hover:border-blue-300 tw:transition-colors"
                        icon="fas fa-pen tw:text-xs"
                        title="Editar nombre"
                        @click.stop="$emit('edit-document', doc)"
                    />
                    <Button
                        ignore-base-classes
                        custom-class="tw:w-6 tw:h-6 tw:rounded-full tw:bg-white/90 tw:shadow-sm tw:border tw:border-gray-200 tw:flex tw:items-center tw:justify-center tw:text-gray-400 tw:hover:text-red-500 tw:hover:border-red-300 tw:transition-colors"
                        icon="fas fa-times tw:text-xs"
                        title="Borrar documento"
                        @click.stop="$emit('delete-document', doc)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, getCurrentInstance } from 'vue';
    import axios from 'axios';
    import Button from '@/modules/admin/ui/Button.vue';
    import BorrarModalComponent from '@/modules/admin/ui/BorrarModalComponent.vue';
    import { withAdminCredentials } from '@/services/admin/adminApiClient';

    const props = defineProps({
        url: { type: String, required: true },
        width: { type: Number, default: null },
        height: { type: Number, default: null },
        acceptedFiles: { type: String, default: 'image/jpeg,image/png' },
        maxFilesize: { type: Number, default: 20 },
        currentImage: { type: String, default: '' },
        preview: { type: String, default: '' },
        // Document mode
        documents: { type: Array, default: null },
        // External state overrides (for parent-controlled uploads)
        uploading: { type: Boolean, default: false },
        uploadingName: { type: String, default: 'Subiendo archivo...' },
        uploadError: { type: String, default: '' },
    });

    const emit = defineEmits(['success', 'removed', 'edit-document', 'delete-document', 'file-selected', 'view-document', 'download-document']);

    const { proxy } = getCurrentInstance();

    const fileInput = ref(null);
    const uploadedResponse = ref(null);
    const localPreview = ref(null);
    const removed = ref(false);
    const dragCounter = ref(0);
    const isDragging = computed(() => dragCounter.value > 0);

    // Internal upload state (only used when not overridden by props)
    const internalUploading = ref(false);
    const internalUploadError = ref('');

    // Use external state when provided, otherwise internal
    const effectiveUploading = computed(() => props.uploading || internalUploading.value);
    const effectiveUploadError = computed(() => props.uploadError || internalUploadError.value);

    // Accept formats
    const acceptFormatsInput = computed(() => {
        return props.acceptedFiles;
    });

    const acceptedHint = computed(() => {
        const exts = props.acceptedFiles.split(',').map(t => {
            const trimmed = t.trim();
            // Handle extension-based formats like ".pdf"
            if (trimmed.startsWith('.')) return trimmed.slice(1).toUpperCase();
            return trimmed.replace('image/', '').toUpperCase();
        }).join(', ');
        let hint = `Formatos: ${exts}`;
        if (props.width && props.height) hint += ` | Medidas: ${props.width}×${props.height}px`;
        if (!props.documents) {
            hint += ` — Máx. ${props.maxFilesize} MB`;
        } else {
            hint += ' — Máx. 10 MB';
        }
        return hint;
    });

    // Dropzone state classes (matching DatosPersonalesCard hardcoded styles)
    const dropzoneClasses = computed(() => {
        if (effectiveUploading.value) return 'tw:border-blue-300 tw:bg-blue-50/50';
        if (effectiveUploadError.value) return 'tw:border-red-400 tw:bg-red-50';
        if (isDragging.value) return 'tw:border-blue-400 tw:bg-blue-50';
        return 'tw:border-gray-300 tw:bg-gray-50 tw:hover:border-blue-400 tw:hover:bg-blue-50/30 focus:tw:border-blue-400 focus:tw:ring-2 focus:tw:ring-blue-200';
    });

    const previewUrl = computed(() => {
        if (removed.value) return null;
        // Mientras haya un blob local (tras subida), darle prioridad
        // para evitar mostrar rutas relativas que el navegador no resuelve.
        if (localPreview.value) return localPreview.value;
        return props.preview || props.currentImage || null;
    });

    function esImagen(ruta) {
        return /\.(png|jpg|jpeg)$/i.test(ruta);
    }

    function handleDragLeave() {
        dragCounter.value--;
        if (dragCounter.value <= 0) dragCounter.value = 0;
    }

    function triggerFileInput() {
        if (!effectiveUploading.value) fileInput.value?.click();
    }

    async function onFileSelected(event) {
        const file = event.target.files?.[0];
        if (!file) return;

        // If the parent handles upload, just emit the file
        if (props.documents) {
            emit('file-selected', file);
            return;
        }

        await uploadFile(file);
    }

    function onDrop(event) {
        dragCounter.value = 0;
        const file = event.dataTransfer?.files?.[0];
        if (!file) return;

        if (props.documents) {
            emit('file-selected', file);
            return;
        }

        uploadFile(file);
    }

    async function uploadFile(file) {
        internalUploadError.value = '';
        removed.value = false;

        // Validate dimensions (single image mode)
        if (props.width && props.height) {
            const valid = await validateDimensions(file);
            if (!valid) {
                internalUploadError.value = `La imagen debe medir exactamente ${props.width}×${props.height}px.`;
                return;
            }
        }

        internalUploading.value = true;

        try {
            const formData = new FormData();
            formData.append('file', file);

            const url = withAdminCredentials(props.url);
            const response = await axios.post(url, formData);

            uploadedResponse.value = response.data;
            if (localPreview.value?.startsWith('blob:')) {
                URL.revokeObjectURL(localPreview.value);
            }
            localPreview.value = URL.createObjectURL(file);
            emit('success', file, response.data);
        } catch (error) {
            internalUploadError.value = 'Error al subir el archivo. Inténtalo de nuevo.';
            if (process.env.NODE_ENV !== 'production') {
                console.error('[Dropzone] Upload error:', error);
            }
        } finally {
            internalUploading.value = false;
        }
    }

    function validateDimensions(file) {
        return new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = e => {
                const img = new Image();
                img.onload = () => {
                    resolve(img.width === props.width && img.height === props.height);
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    }

    function showDeleteConfirm() {
        proxy.$modal.show('dropzone-delete-image');
    }

    function confirmRemove() {
        removeImage();
    }

    function removeImage() {
        removed.value = true;
        if (localPreview.value?.startsWith('blob:')) {
            URL.revokeObjectURL(localPreview.value);
        }
        localPreview.value = null;
        uploadedResponse.value = null;
        if (fileInput.value) fileInput.value.value = '';
        emit('removed');
    }
</script>

<script>
    export default {
        inheritAttrs: false,
    };
</script>
